import type { GridColDef } from '@mui/x-data-grid';
import { type BaseIntegrationEntity, type Currency } from '@vy/types';
import { isNotNullOrUndefined, isNumber, Optional } from '@vy/utils';
import { Price } from '../../../../../components/design/Price';
import { VyTableCell } from '../../../../../components/new-design/table/vy-table/VyTableCell';
import { getBillingIntervalLabel } from '../../ContractSummaryDetails/contract-summary-details-utils/get-billing-interval-label';
import { CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS } from '../contract-fixed-fees-const/contract-fixed-fees-columns-keys';
import type { ContractFixedFeesColumns } from '../contract-fixed-fees-types/contract-fixed-fees-columns';
import { formatPaymentLabel, ONE_TIME_FEE_LABEL } from './get-contract-fixed-fees-rows';

type GetContractFixedFeesColumnsOptions = {
  isAnrokEnabled?: boolean;
  isConnactEnabled?: boolean;
};

export const getContractFixedFeesColumns = (
  currency: Currency,
  options: GetContractFixedFeesColumnsOptions = {},
): GridColDef<ContractFixedFeesColumns>[] => {
  const { isAnrokEnabled = false, isConnactEnabled = false } = options;

  const baseColumns: GridColDef<ContractFixedFeesColumns>[] = [
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRODUCT_NAME,
    headerName: 'Product Name',
    headerClassName: 'text-sm',
    headerAlign: 'left',
    align: 'left',
    flex: 1,
    minWidth: 180,
    hideable: false,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRICING_TYPE,
    headerName: 'Pricing Type',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    hideable: false,
    renderCell: ({ row: { pricingType } }) => <VyTableCell value={pricingType} />,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 160,
    hideable: false,
    renderCell: ({ row }) =>
      isNumber(row.amount) ? (
        <Price currency={currency}>{row.amount}</Price>
      ) : (
        <VyTableCell value={row.amount} containerClassName="justify-center" />
      ),
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.DISCOUNT,
    headerName: 'Discount',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    hideable: false,
    renderCell: ({ row }) => {
      return <VyTableCell value={row.discount} />;
    },
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.QUANTITY,
    headerName: 'Quantity',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 80,
    hideable: false,
    renderCell: ({ row: { quantity } }) => <VyTableCell value={quantity} />,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRICE_PER_UNIT,
    headerName: 'Price Per Unit',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { pricePerUnit } }) => <Price currency={currency}>{pricePerUnit}</Price>,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.BILLING_INTERVAL,
    headerName: 'Frequency',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { billingInterval, pricingType } }) =>
      pricingType === ONE_TIME_FEE_LABEL ? (
        <VyTableCell value="--" />
      ) : (
        <VyTableCell
          value={isNotNullOrUndefined(billingInterval) ? getBillingIntervalLabel(billingInterval) : undefined}
        />
      ),
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.SCHEDULE_START_DATE,
    headerName: 'Start Date',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { scheduleStartDate } }) => <VyTableCell value={scheduleStartDate} />,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.SCHEDULE_END_DATE,
    headerName: 'End Date',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { scheduleEndDate } }) => <VyTableCell value={scheduleEndDate} />,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PAYMENT_TERM,
    headerName: 'Payment',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { paymentTerm } }) => (
      <VyTableCell value={isNotNullOrUndefined(paymentTerm) ? formatPaymentLabel(paymentTerm) : undefined} />
    ),
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.ERP,
    headerName: 'ERP ID',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { erp } }) => <VyTableCell value={getErpName(erp)} />,
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.INSTALLMENTS,
    headerName: 'Installments',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { installments, shouldShowInstallments } }) => (
      <VyTableCell value={shouldShowInstallments ? installments : undefined} />
    ),
  },
  {
    field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.PRO_RATE,
    headerName: 'Pro Rate',
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 120,
    renderCell: ({ row: { proRate } }) => <VyTableCell value={proRate} />,
  },
  ];

  const optionalColumns: GridColDef<ContractFixedFeesColumns>[] = [];

  if (isConnactEnabled) {
    optionalColumns.push(
      {
        field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.MARKETPLACE_PRODUCT_ID,
        headerName: 'MP Product ID',
        headerClassName: 'text-sm',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 180,
      },
      {
        field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.MARKETPLACE_DIMENSION,
        headerName: 'MP Dimension',
        headerClassName: 'text-sm',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 180,
      },
    );
  }

  if (isAnrokEnabled) {
    optionalColumns.push({
      field: CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.ANROK_ID,
      headerName: 'Anrok ID',
      headerClassName: 'text-sm',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 140,
    });
  }

  if (optionalColumns.length > 0) {
    baseColumns.splice(
      baseColumns.findIndex((col) => col.field === CONTRACT_PAGE_FIXED_FEES_COLUMNS_KEYS.ERP) + 1,
      0,
      ...optionalColumns,
    );
  }

  return baseColumns;
};

export function getErpName(erp: Optional<BaseIntegrationEntity>) {
  const { integrationExternalId, metadata } = erp ?? {};
  const erpName = metadata?.name;
  const erpId = metadata?.displayId ?? integrationExternalId;

  if (isNotNullOrUndefined(erpName) && isNotNullOrUndefined(erpId)) {
    return `${erpName} - ${erpId}`;
  }

  if (isNotNullOrUndefined(erpName)) {
    return erpName;
  }

  if (isNotNullOrUndefined(erpId)) {
    return erpId;
  }

  return undefined;
}
